import { Fragment, cloneElement, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { changeURLParams, generateModifiedNextURL, useRouter } from '@medifind/router';
import { Button, MFLink, Skeleton } from '@medifind/shared-basic-components';
import { MFLazyLoad } from '@medifind/shared-basic-components';
import { eventDispatch, numberWithCommas, scrollToId } from '@medifind/utils';
import { useAuthentication } from '@medifind/zustand';
import styles from './Grid.module.scss';

const loginPopup = (errorMessage, errorTitle, nextUrl) => {
  eventDispatch('activateAuth', {
    errorTitle,
    errorMessage,
    authType: 'login',
    icon: 'next',
    redirectAction: 'page',
    redirectData: nextUrl,
  });
};

const Grid = ({
  error,
  errorMessage,
  lazyLoad = {},
  items,
  pagination,
  size,
  id,
  maxColumnCount,
  classes = {},
  resultsHeader,
  insertResult = [],
  height,
  noBorder,
  addGutters = true,
  noResultsHtml,
  bottomDisclaimer,
}) => {
  const [gid] = useState(id ? id : `results-grid-${Math.floor(Math.random() * 1000)}`);
  maxColumnCount = maxColumnCount ? maxColumnCount : Math.max(...[1, 2, 3, 5].filter((k) => size % k === 0));

  if (error) {
    return (
      <div id={gid} className={classNames(styles['no-results'], styles['no-results-error'], classes.noResults)}>
        {errorMessage ?? (
          <>
            Internal Server Error <br />
            Try again later - Or try changing your parameters
          </>
        )}
      </div>
    );
  }

  const lazyLoadWrapper = (item, i) => {
    if (Object.keys(lazyLoad).length !== 0 && i >= 2) {
      const { offset, height, resize, noSuspense } = lazyLoad;
      return (
        <Fragment key={`lazy-load-${i}`}>
          <MFLazyLoad offset={offset} height={height} resize={resize} noSuspense={noSuspense}>
            {item}
          </MFLazyLoad>
        </Fragment>
      );
    } else {
      return item;
    }
  };

  const renderInsertResultItem = (element, i) => (typeof element.render === 'function' ? element.render(i) : element.render);
  const renderedInserts = [];

  return (
    <>
      {resultsHeader && resultsHeader}
      {items.length ? (
        <div
          id={gid}
          className={classNames(
            classes['grid'],
            styles['grid'],
            [1, 2, 3, 5].filter((k) => k <= maxColumnCount && size % k === 0).map((k) => styles[`grid-col-${k}`]),
            styles[`grid-max-${maxColumnCount}`],
            { [styles['scroll-container']]: !!height, [styles['no-border']]: noBorder, [styles['add-gutters']]: addGutters },
          )}
          style={{ height }}
        >
          {items.map(lazyLoadWrapper).reduce((p, c, i) => {
            const renderElements = (renderProp) => {
              for (const [index, element] of insertResult
                .filter(
                  (element) =>
                    (typeof element[renderProp] === 'number' && element[renderProp] === i + 1) ||
                    (typeof element[renderProp] === 'function' && element[renderProp](i)) ||
                    (!element.dontShowAtEnd &&
                      i === items.length - 1 &&
                      !renderedInserts.includes(element) &&
                      renderProp === 'renderAfter'),
                )
                .entries()) {
                renderedInserts.push(element);
                p.push(cloneElement(renderInsertResultItem(element, i), { key: `insert_${renderProp}_${i}_${index}}` }));
              }
            };

            renderElements('renderBefore');
            p.push(c);
            renderElements('renderAfter');
            return p;
          }, [])}
        </div>
      ) : (
        <div
          id={`${gid}-no-results`}
          className={classNames(
            classes['noResults'],
            styles['no-results'],
            {
              [styles['no-border']]: noBorder,
              [styles['add-gutters']]: addGutters,
            },
            noResultsHtml && styles['custom-html-no-results'],
          )}
        >
          {noResultsHtml ? (
            noResultsHtml
          ) : (
            <>
              There were no results for your search.
              <br />
              Please expand the search radius and try again.
            </>
          )}
        </div>
      )}
      {bottomDisclaimer}
      {pagination && <Pagination {...pagination} classes={classes} gid={gid} size={size} />}
    </>
  );
};
const paginationLinkTypes = ['link', 'eventWithLinkFallback'];

const Pagination = ({
  page,
  size,
  totalResults,
  onPageChange,
  type = 'event',
  noResults = '0 providers Found',
  urlParamName = 'page',
  gid,
  classes,
  forceScroll,
  scrollOffset = 0,
  scrollerRef,
  requireLogin,
  wideLayout,
}) => {
  const router = useRouter();
  const { profile } = useAuthentication();

  useEffect(() => {
    // Check if current page requires authentication
    if (requireLogin && !profile && page > requireLogin.afterPage) {
      loginPopup(requireLogin.errorMessage, requireLogin.errorTitle, router.location.href);
      changeURLParams(router, { [urlParamName]: requireLogin.afterPage }, { type: 'push' });
    }
  }, [!!profile, page, !!requireLogin]);

  useEffect(() => {
    // if totalResults are less than the size of the grid (most likely due to filtering), go back to the first page
    if (totalResults < size && page > 1 && ['eventWithRouterChange', 'eventWithRouterShallowChange', 'link'].includes(type)) {
      changeURLParams(router, { [urlParamName]: 1 }, { type: type === 'eventWithRouterShallowChange' ? 'replace' : 'push' });
    }
    if (page * size > Math.ceil(totalResults / size) * size) {
      changeURLParams(
        router,
        { [urlParamName]: Math.ceil(totalResults / size) },
        { type: type === 'eventWithRouterShallowChange' ? 'replace' : 'push' },
      );
    }
  }, [totalResults, page, size, router, urlParamName, type]);

  const handlePageChange = (e, func, type) => {
    const newPage = func === 'next' ? page + 1 : page - 1;
    if (func === 'next' && requireLogin && !profile && newPage > requireLogin.afterPage) {
      // Check if next page requires authentication
      loginPopup(requireLogin.errorMessage, requireLogin.errorTitle, generateModifiedNextURL(router, { [urlParamName]: newPage }));
    } else {
      if (['eventWithRouterChange', 'eventWithRouterShallowChange', 'event', 'eventWithLinkFallback', 'link'].includes(type)) {
        if (['eventWithRouterChange', 'eventWithRouterShallowChange', 'link'].includes(type)) {
          changeURLParams(router, { [urlParamName]: newPage }, { type: type === 'eventWithRouterShallowChange' ? 'replace' : 'push' });
        }
        onPageChange && onPageChange(newPage, type);
      }
      if (['eventWithRouterChange', 'eventWithRouterShallowChange', 'event', 'eventWithLinkFallback', 'link'].includes(type)) {
        if (window.innerWidth < 992 || forceScroll) scrollToId(gid, 0, scrollOffset, scrollerRef?.current);
      }
    }
  };
  return (
    <div className={classNames(styles['pagination'], { [styles['wide-layout']]: wideLayout }, classes['pagination'])}>
      {totalResults == null ? (
        <Skeleton width={250} heigh={20} />
      ) : (
        <>
          <div className={classNames(styles['pagination__label'], classes['pagination__label'])}>
            {totalResults > 0
              ? `Showing ${(page - 1) * size + 1}-${numberWithCommas(Math.min(page * size, totalResults))} of ${numberWithCommas(
                  totalResults,
                )}`
              : noResults}
          </div>
          <div
            className={classNames(styles['pagination__buttons'], { [styles['wide-layout']]: wideLayout }, classes['pagination__buttons'])}
          >
            {/* dont show either button if no more stuff at all */}
            {totalResults > 0 && page > 1 && (
              <PaginationLinks page={page} type={type} urlParamName={urlParamName} onClick={(e) => handlePageChange(e, 'previous', type)}>
                <Button
                  {...(paginationLinkTypes.includes(type) ? {} : { onClick: (e) => handlePageChange(e, 'previous', type) })}
                  color="red"
                  label="Previous"
                />
              </PaginationLinks>
            )}
            {page * size < totalResults && (
              <PaginationLinks page={page + 1} type={type} urlParamName={urlParamName} onClick={(e) => handlePageChange(e, 'next', type)}>
                <Button
                  {...(paginationLinkTypes.includes(type) ? {} : { onClick: (e) => handlePageChange(e, 'next', type) })}
                  color="blue"
                  label="Next"
                />
              </PaginationLinks>
            )}
          </div>
        </>
      )}
    </div>
  );
};
const PaginationLinks = ({ page, children, type, urlParamName, onClick }) => {
  // const isFirstPage = page - 1 === 0;
  const router = useRouter();
  const href = generateModifiedNextURL(router, { [urlParamName]: page });
  return paginationLinkTypes.includes(type) ? (
    <MFLink
      clickOverride={(e) => {
        onClick && onClick();
        e.preventDefault();
      }}
      to={href}
    >
      {children}
    </MFLink>
  ) : (
    children
  );
};
Grid.propTypes = {
  error: PropTypes.bool,
  errorMessage: PropTypes.object,
  insertResult: PropTypes.array,
  resultsHeader: PropTypes.node,
  noResultsHtml: PropTypes.node,
  lazyLoad: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.node).isRequired,
  id: PropTypes.string,
  size: PropTypes.number.isRequired,
  height: PropTypes.number,
  maxColumnCount: PropTypes.oneOf([1, 2, 3, 5]),
  pagination: PropTypes.shape({
    type: PropTypes.oneOf(['eventWithRouterChange', 'eventWithRouterShallowChange', 'event', 'link', 'eventWithLinkFallback']),
    page: PropTypes.number.isRequired,
    totalResults: PropTypes.number,
    onPageChange: PropTypes.func,
    forceScroll: PropTypes.bool,
    scrollerRef: PropTypes.any,
    scrollOffset: PropTypes.number,
    urlParamName: PropTypes.string,
    gid: PropTypes.string, // Defaults to "page"
    requireLogin: PropTypes.shape({
      errorMessage: PropTypes.string.isRequired,
      errorTitle: PropTypes.string.isRequired,
      afterPage: PropTypes.number.isRequired,
    }),
    wideLayout: PropTypes.bool,
  }),
  classes: PropTypes.shape({
    grid: PropTypes.string,
    filters: PropTypes.string,
    noResults: PropTypes.string,
  }),
  noBorder: PropTypes.bool,
  addGutters: PropTypes.bool,
  roadBlock: PropTypes.any,
  bottomDisclaimer: PropTypes.node,
};

export { Grid as default, Grid };
